<template>
  <div class="customer-create main-content">
    <form action="" @submit.prevent="submitSupplierData">
      <div class="page-header">
        <h3 class="page-title">{{ $t('suppliers.new_supplier') }}</h3>
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/anchor/dashboard">{{ $t('general.home') }}</router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/anchor/suppliers">{{ $tc('suppliers.supplier', 2) }}</router-link>
          </li>
          <li class="breadcrumb-item">
            {{ $t('suppliers.new_supplier') }}
          </li>
        </ol>
        <div class="page-actions header-button-container">
          <base-button
            :loading="isLoading"
            :disabled="isLoading"
            :tabindex="23"
            icon="save"
            color="theme"
            type="submit"
          >
            {{ $t('suppliers.save_supplier') }}
          </base-button>
        </div>
      </div>
      <div class="customer-card card">
        <div class="card-body">
          <div class="row">
            <div class="section-title col-sm-2" >{{ $tc('suppliers.supplier', 2) }}</div>
            <div class="col-sm-10">
              <div class="form-group">
                <base-select
                  v-model="referUser"
                  :options="supplierUsersType"
                  :searchable="true"
                  :show-labels="false"
                  :allow-empty="true"
                  :tabindex="8"
                  :placeholder="$t('suppliers.select_supplier')"
                  label="company_name"
                  track-by="id"
                  @select="referData"
                />
                <div v-if="$v.referUser.$error">
                  <span v-if="!$v.referUser.required" class="text-danger">
                    {{ $tc('validation.required') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <hr>
          <div class="row">
            <div class="section-title col-sm-2">{{ $t('suppliers.basic_info') }}</div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.display_name') }}</label><span class="text-danger"> *</span>
                <div v-if="formData.name" class="base-read-box base-read-box-field">
                  <span v-html="formData.name"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.email') }}</label>
                <div v-if="formData.email" class="base-read-box base-read-box-field">
                  <span v-html="formData.email"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.primary_currency') }}</label>
                <div v-if="currency" class="base-read-box base-read-box-field">
                  <span v-html="currencyNameWithCode(currency)"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('general.payment_term') }}</label><span class="text-danger"> *</span>
                <base-input
                  v-model.trim="formData.tenor"
                  :label="$t('general.tenor')"
                  type="text"
                  tab-index="2"
                />
                <div v-if="$v.formData.tenor.$error">
                  <span v-if="!$v.formData.tenor.required" class="text-danger">
                    {{ $tc('validation.required') }}
                  </span>
                </div>
              </div>

            </div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.primary_contact_name') }}</label>
                <div v-if="formData.contact_name" class="base-read-box base-read-box-field">
                  <span v-html="formData.contact_name"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.phone') }}</label>
                <div v-if="formData.phone" class="base-read-box base-read-box-field">
                  <span v-html="formData.phone"/>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.website') }}</label>
                <div v-if="formData.website" class="base-read-box base-read-box-field">
                  <span v-html="formData.website"/>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('general.supplier_id') }}</label><span class="text-danger"> *</span>
                <base-input
                  v-model.trim="formData.supplier_id"
                  :label="$t('general.supplier_id')"
                  aria-required="true"
                  type="text"
                  tab-index="2"
                />
                <div v-if="$v.formData.supplier_id.$error">
                  <span v-if="!$v.formData.supplier_id.required" class="text-danger">
                    {{ $tc('validation.required') }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <hr> <!-- first row complete  -->
          <div class="row">
            <div class="section-title col-sm-2">{{ $t('suppliers.billing_address') }}</div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.name') }}</label>
                <div v-if="billing.name" class="base-read-box base-read-box-field">
                  <span v-html="billing.name"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.state') }}</label>
                <div v-if="billing.state" class="base-read-box base-read-box-field">
                  <span v-html="billing.state"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.address') }}</label>
                <div v-if="billing.address_street_1" class="base-read-box base-read-box-field">
                  <span v-html="billing.address_street_1"/>
                </div>
                <div v-if="billing.address_street_2" class="base-read-box base-read-box-field">
                  <span v-html="billing.address_street_2"/>
                </div>
              </div>
            </div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.country') }}</label>
                <div v-if="billing_country && billing_country.name" class="base-read-box base-read-box-field">
                  <span v-html="billing_country.name"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.city') }}</label>
                <div v-if="billing.city" class="base-read-box base-read-box-field">
                  <span v-html="billing.city"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.phone') }}</label>
                <div v-if="billing.phone" class="base-read-box base-read-box-field">
                  <span v-html="billing.phone"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.zip_code') }}</label>
                <div v-if="billing.zip" class="base-read-box base-read-box-field">
                  <span v-html="billing.zip"/>
                </div>
              </div>
            </div>
          </div>
          <hr> <!-- second row complete  -->
          <div class="row">
            <div class="section-title col-sm-2">
              {{ $t('suppliers.shipping_address') }}
            </div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.name') }}</label>
                <div v-if="shipping.name" class="base-read-box base-read-box-field">
                  <span v-html="shipping.name"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.state') }}</label>
                <div v-if="shipping.state" class="base-read-box base-read-box-field">
                  <span v-html="shipping.state"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.address') }}</label>
                <div v-if="shipping.address_street_1" class="base-read-box base-read-box-field">
                  <span v-html="shipping.address_street_1"/>
                </div>
                <div v-if="shipping.address_street_2" class="base-read-box base-read-box-field">
                  <span v-html="shipping.address_street_2"/>
                </div>
              </div>
            </div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.country') }}</label>
                <div v-if="shipping_country && shipping_country.name" class="base-read-box base-read-box-field">
                  <span v-html="shipping_country.name"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.city') }}</label>
                <div v-if="shipping.city" class="base-read-box base-read-box-field">
                  <span v-html="shipping.city"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.phone') }}</label>
                <div v-if="shipping.phone" class="base-read-box base-read-box-field">
                  <span v-html="shipping.phone"/>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('suppliers.zip_code') }}</label>
                <div v-if="shipping.zip" class="base-read-box base-read-box-field">
                  <span v-html="shipping.zip"/>
                </div>
              </div>
              <div class="form-group collapse-button-container">
                <base-button
                  :tabindex="23"
                  icon="save"
                  color="theme"
                  class="collapse-button"
                  type="submit"
                >
                  {{ $t('suppliers.save_supplier') }}
                </base-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import MultiSelect from 'vue-multiselect'
import { validationMixin } from 'vuelidate'
import AddressStub from '../../stub/address'
const {required} = require('vuelidate/lib/validators')

export default {
  components: { MultiSelect },
  mixins: [validationMixin],
  data () {
    return {
      userType: null,
      supplierUsersType: [],
      currencyID: '',
      referUser: null,
      isCopyFromBilling: false,
      isLoading: false,
      formData: {
        parent_account_id: null,
        company_name: null,
        name: null,
        contact_name: null,
        email: null,
        phone: null,
        currency_id: null,
        website: null,
        supplier_id: null,
        tenor: null,
        addresses: []
      },
      currency: null,
      billing: {
        name: null,
        country_id: null,
        state: null,
        city: null,
        phone: null,
        zip: null,
        address_street_1: null,
        address_street_2: null,
        type: 'billing'
      },
      shipping: {
        name: null,
        country_id: null,
        state: null,
        city: null,
        phone: null,
        zip: null,
        address_street_1: null,
        address_street_2: null,
        type: 'shipping'
      },
      currencyList: [],

      billing_country: null,
      shipping_country: null
    }
  },
  validations: {
    referUser: {
      required
    },
    formData: {
      tenor: { required },
      supplier_id: { required }
    }
  },
  computed: {
    ...mapGetters('customer', [
      'totalCustomers',
      'customers'
    ]),
    ...mapGetters('user', [
      'defaultCountry'
    ]),
    ...mapGetters('currency', [
      'defaultCurrency',
      'currencies'
    ]),
    hasBillingAdd () {
      let billing = this.billing
      if (
        billing.name ||
        billing.country_id ||
        billing.state ||
        billing.city ||
        billing.phone ||
        billing.zip ||
        billing.address_street_1 ||
        billing.address_street_2) {
        return true
      }
      return false
    },
    hasShippingAdd () {
      let shipping = this.shipping
      if (
        shipping.name ||
        shipping.country_id ||
        shipping.state ||
        shipping.city ||
        shipping.phone ||
        shipping.zip ||
        shipping.address_street_1 ||
        shipping.address_street_2) {
        return true
      }
      return false
    }
  },
  watch: {
    billing_country (newCountry) {
      if (newCountry) {
        this.billing.country_id = newCountry.id
        this.isDisabledBillingState = false
      } else {
        this.billing.country_id = null
      }
    },
    shipping_country (newCountry) {
      if (newCountry) {
        this.shipping.country_id = newCountry.id
        return true
      } else {
        this.shipping.country_id = null
      }
    }
  },
  created () {
    if (localStorage.getItem('account_type') === 'funder') {
      this.userType = 'funder'
    } else if (localStorage.getItem('account_type') === 'supplier') {
      this.userType = 'supplier'
    } else if (localStorage.getItem('account_type') === 'anchor') {
      this.userType = 'anchor'
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    currencyNameWithCode (currency) {
      return `${currency.code} - ${currency.name}`
    },
    ...mapActions('supplier', [
      'addSupplier',
      'fetchCreateSupplier',
      'fetchViewSupplier'
    ]),
    referData (val) {
      this.formData.parent_account_id = val.id
      this.formData.company_name = val.company_name
      this.formData.name = val.company_name
      this.formData.email = val.email
      this.formData.contact_name = val.contact_name
      this.formData.phone = val.phone
      this.formData.website = val.website
      this.currencyID = val.currency_id
      this.currency = val.account.currency
      this.formData.tenor = val.tenor
      this.formData.supplier_id = val.supplier_id

      if (val.account.billing_address && val.account.billing_address.name) {
        this.billing.name = val.account.billing_address.name
      }
      if (val.account.billing_address && val.account.billing_address.country_id) {
        this.billing.country_id = val.account.billing_address.country_id
      }
      if (val.account.billing_address && val.account.billing_address.zip) {
        this.billing.zip = val.account.billing_address.zip
      }
      if (val.account.billing_address && val.account.billing_address.phone) {
        this.billing.phone = val.account.billing_address.phone
      }
      if (val.account.billing_address && val.account.billing_address.fax) {
        this.billing.fax = val.account.billing_address.fax
      }
      if (val.account.billing_address && val.account.billing_address.state) {
        this.billing.state = val.account.billing_address.state
      }
      if (val.account.billing_address && val.account.billing_address.address_street_1) {
        this.billing.address_street_1 = val.account.billing_address.address_street_1
      }
      if (val.account.billing_address && val.account.billing_address.address_street_2) {
        this.billing.address_street_2 = val.account.billing_address.address_street_2
      }
      if (val.account.shipping_address && val.account.shipping_address.name) {
        this.shipping.name = val.account.shipping_address.name
      }
      if (val.account.shipping_address && val.account.shipping_address.address_street_1) {
        this.shipping.address_street_1 = val.account.shipping_address.address_street_1
      }
      if (val.account.shipping_address && val.account.shipping_address.address_street_2) {
        this.shipping.address_street_2 = val.account.shipping_address.address_street_2
      }
      if (val.account.shipping_address && val.account.shipping_address.city) {
        this.shipping.city = val.account.shipping_address.city
      }
      if (val.account.shipping_address && val.account.shipping_address.state) {
        this.shipping.state = val.account.shipping_address.state
      }
      if (val.account.shipping_address && val.account.shipping_address.country_id) {
        this.shipping.country_id = val.account.shipping_address.country_id
      }
      if (val.account.shipping_address && val.account.shipping_address.zip) {
        this.shipping.zip = val.account.shipping_address.zip
      }
      if (val.account.shipping_address && val.account.shipping_address.phone) {
        this.shipping.phone = val.account.shipping_address.phone
      }
      if (val.account.shipping_address && val.account.shipping_address.fax) {
        this.shipping.fax = val.account.shipping_address.fax
      }
      if (val.account.billing_address && val.account.billing_address.country) {
        this.shipping_country = val.account.billing_address.country
      }
      if (val.account.shipping_address && val.account.shipping_address.country) {
        this.billing_country = val.account.shipping_address.country
      }
    },

    async loadData () {
      let response = await this.fetchCreateSupplier()
      this.supplierUsersType = response.data.suppliers
    },
    copyAddress (val) {
      if (val === true) {
        this.isCopyFromBilling = true
        this.shipping = {...this.billing, type: 'shipping'}
        this.shipping_country = this.billing_country
        this.shipping_state = this.billing_state
        this.shipping_city = this.billing_city
      } else {
        this.shipping = {...AddressStub, type: 'shipping'}
        this.shipping_country = null
        this.shipping_state = null
        this.shipping_city = null
      }
    },
    async submitSupplierData () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return true
      }
      if (this.hasBillingAdd && this.hasShippingAdd) {
        this.formData.addresses = [{...this.billing}, {...this.shipping}]
      } else {
        if (this.hasBillingAdd) {
          this.formData.addresses = [{...this.billing}]
        }
        if (this.hasShippingAdd) {
          this.formData.addresses = [{...this.shipping}]
        }
      }

      this.isLoading = true
      let response = await this.addSupplier(this.formData)

      if (response.data.success) {
        window.toastr['success'](this.$t('suppliers.created_message'))
        this.$router.push(`/anchor/suppliers`)
        this.isLoading = false
        return true
      }
      if (response.data.error) {
        this.isLoading = false
        window.toastr['error'](response.data.message)
      }
    }
  }
}
</script>
